import {
  Component,
  DestroyRef,
  HostListener,
  Inject,
  OnInit,
  PLATFORM_ID,
  inject,
  signal,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ersConstants } from '@rcs/utils';
import { ApiNotificationService } from '@rcs/notify';
import { ErsIdentityService } from './services/ers-identity/ers-identity.service';
import { TabManagerService } from './base/shell/tab-manager.service';
import { NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  isStaleBuildChunkError,
  shouldAttemptStaleBuildReload,
} from './services/stale-build/stale-build';
import { isTenantSwitch } from './services/tenant-sync/tenant-sync';

@Component({
  selector: 'ers-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  private readonly _identityService = inject(ErsIdentityService);
  private readonly _router = inject(Router);
  private readonly _tabs = inject(TabManagerService);
  private readonly _notifications = inject(ApiNotificationService);
  private readonly _destroyRef = inject(DestroyRef);

  /** Desktop (Electron) chrome state, driving the thin title strip. */
  readonly isDesktop = signal(false);
  readonly isMaximized = signal(false);
  private _electron: {
    invoke?: (channel: string) => Promise<unknown>;
    on?: (channel: string, cb: (...args: unknown[]) => void) => () => void;
  } | null = null;
  private _heartbeatHandle: ReturnType<typeof setInterval> | null = null;

  title = 'ers';

  constructor(@Inject(PLATFORM_ID) private readonly platformId: object) {}

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      const splash = document.getElementById('ers-splash');
      if (splash) {
        splash.classList.add('fade-out');
        setTimeout(() => splash.remove(), 400);
      }
      this._applyDesktopChrome();
      this._startSessionHeartbeat();
      this._clearLeakedScrollLocksOnNavigation();
      this._recoverFromStaleBuildChunks();
    }
  }

  /**
   * A tab left open across a deploy still runs the previous build, whose lazy
   * route chunks no longer exist on the server. Clicking a module link then fails
   * silently — the URL does not change and nothing renders — and every other
   * not-yet-loaded module link fails the same way, so the left-hand nav looks
   * dead until the user reloads by hand. Reload for them, landing on the URL they
   * asked for. (Bug 32721.)
   */
  private _recoverFromStaleBuildChunks(): void {
    this._router.events
      .pipe(
        filter((event): event is NavigationError => event instanceof NavigationError),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((event) => {
        if (
          !isStaleBuildChunkError(event.error) ||
          !shouldAttemptStaleBuildReload(sessionStorage)
        ) {
          return;
        }

        // assign(), not reload(): reload() would re-fetch the URL the user is
        // still sitting on and drop the navigation they actually asked for.
        window.location.assign(event.url);
      });
  }

  private _clearLeakedScrollLocksOnNavigation(): void {
    this._router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(() => {
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
      });
  }

  /**
   * When running in the Electron desktop shell the window is frameless and the
   * app header doubles as the draggable title strip (see styles.scss). Tag the
   * document so that chrome activates, and add the platform so the OS window
   * controls' reserved space is placed on the correct side (right on Windows /
   * Linux, left for the macOS traffic lights). A no-op in a normal browser.
   */
  private _applyDesktopChrome(): void {
    const electron = (
      window as unknown as {
        electron?: {
          isDesktop?: boolean;
          invoke?: (channel: string) => Promise<unknown>;
          on?: (channel: string, cb: (...args: unknown[]) => void) => () => void;
        };
      }
    ).electron;
    if (!electron?.isDesktop) return;

    this._electron = electron;
    this.isDesktop.set(true);

    const root = document.documentElement;
    root.classList.add('is-desktop');

    electron
      .invoke?.('app:platform-info')
      .then((info) => {
        const platform = (info as { platform?: string } | null)?.platform ?? '';
        const cls =
          platform === 'darwin'
            ? 'platform-mac'
            : platform === 'win32'
              ? 'platform-win'
              : 'platform-linux';
        root.classList.add(cls);
      })
      .catch(() => {
        /* platform info unavailable; the strip still works */
      });

    this._refreshMaximized();

    // Floating strip on the auth screens; it docks (and shrinks) once inside
    // the app. Driven by the route so it tracks login / logout.
    const updateAuthed = () => {
      const onAuth = /\/(login|forgot|reset|register|verify)/i.test(
        this._router.url || ''
      );
      root.classList.toggle('is-authed', !onAuth);
    };
    this._router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(updateAuthed);
    updateAuthed();
  }

  private _refreshMaximized(): void {
    this._electron?.invoke?.('window:is-maximized')
      .then((v) => this.isMaximized.set(v === true))
      .catch(() => undefined);
  }

  minimizeWindow(): void {
    this._electron?.invoke?.('window:minimize').catch(() => undefined);
  }

  toggleMaximizeWindow(): void {
    this._electron?.invoke?.('window:toggle-maximize')
      .then((v) => this.isMaximized.set(v === true))
      .catch(() => undefined);
  }

  closeWindow(): void {
    this._electron?.invoke?.('window:close').catch(() => undefined);
  }

  private _startSessionHeartbeat(): void {
    const ping = () => {
      if (
        this._identityService.isTokenExpired() &&
        this._identityService.retrieveItemFromLs(ersConstants.SELECTED_TENANT)
      ) {
        this._identityService.pingSession().subscribe();
      }
    };

    ping(); // register/update session in DB immediately; don't wait 30s
    this._heartbeatHandle = setInterval(ping, 30_000);

    this._destroyRef.onDestroy(() => {
      if (this._heartbeatHandle !== null) {
        clearInterval(this._heartbeatHandle);
      }
    });
  }

  @HostListener('window:storage', ['$event'])
  onStorageChanged(event: StorageEvent): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const isJwtRemoved =
      event.key === ersConstants.ERS_JWT_TOKEN && event.newValue === null;
    const isStorageCleared = event.key === null && event.oldValue !== null;

    if (isJwtRemoved || isStorageCleared) {
      this._identityService.logOutRequest();
      this._tabs.disconnect();
      this._notifications.clear();
      this._router.navigateByUrl('/login');
      return;
    }

    /*
     * Another tab changed organisation.
     *
     * The interceptor reads the selected tenant per request while the chrome
     * and the store read it once at load, so without this a tab goes on
     * showing one organisation's name, menu and permissions while its
     * requests carry another's - which on DEV meant a tab headed "Rectitude
     * Consulting Services" whose every read came back empty, and an
     * organisation switcher that refused to help because the tab believed it
     * was already there.
     *
     * Reading the wrong tenant's data is the visible half; writing to it is
     * the dangerous one. Reloading is blunt, and it is what the in-tab switch
     * effectively does: the tenant decides the menu, the role permissions and
     * the scope of every store, and re-deriving all of that in place is a
     * larger promise than it looks.
     */
    if (isTenantSwitch(event, ersConstants.SELECTED_TENANT)) {
      window.location.reload();
    }
  }
}
